import { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { BookOpen, Clock, Lightbulb, Warning, ChalkboardTeacher, Sparkle, ArrowRight, CaretRight, Printer, Crown, Lock } from "@phosphor-icons/react";

export default function LessonPage() {
    const { courseId } = useParams();
    const [params] = useSearchParams();
    const topic = params.get("topic");
    const nav = useNavigate();
    const { user } = useAuth();
    const [lesson, setLesson] = useState(null);
    const [loading, setLoading] = useState(true);
    const [course, setCourse] = useState(null);
    const [upsellOpen, setUpsellOpen] = useState(false);

    useEffect(() => {
        if (!topic) { nav(`/courses/${courseId}`); return; }
        setLoading(true);
        Promise.all([
            api.get(`/courses/${courseId}`),
            api.get(`/lessons/${courseId}/topic`, { params: { topic } }),
        ]).then(([c, l]) => { setCourse(c.data); setLesson(l.data); })
          .catch(err => {
              toast.error(err?.response?.data?.detail || "Could not load lesson");
              nav(`/courses/${courseId}`);
          })
          .finally(() => setLoading(false));
    }, [courseId, topic, nav]);

    if (loading) {
        return (
            <div className="min-h-screen">
                <Navbar />
                <div className="mx-auto max-w-3xl px-6 py-32 text-center">
                    <div className="dot-pulse mx-auto"></div>
                    <div className="mt-6 font-serif text-2xl text-ink">Wibpro AI is preparing your lesson…</div>
                    <div className="text-sm text-ink/60 mt-2">Curated by Claude Sonnet 4.6 — this takes ~10-15 seconds the first time, then it's instant.</div>
                </div>
            </div>
        );
    }
    if (!lesson) return null;
    const c = lesson.content;

    const handlePrint = async () => {
        // Free users see the upsell dialog; premium/admin can print directly.
        if (!user?.is_premium && !user?.is_admin) {
            setUpsellOpen(true);
            return;
        }
        try {
            // Server-side record + gate (defence in depth)
            await api.get(`/lessons/${courseId}/topic/download`, { params: { topic } });
        } catch (err) {
            if (err?.response?.status === 402) {
                setUpsellOpen(true);
                return;
            }
            // Not fatal — still allow print
        }
        document.title = `${c.title || topic} · Wibpro Academy`;
        setTimeout(() => window.print(), 50);
    };

    return (
        <div className="min-h-screen" data-testid="lesson-page">
            <Navbar />

            <section className="mx-auto max-w-4xl px-6 py-12 print-lesson">
                <div className="flex items-center gap-2 text-xs text-ink/50 mb-4 no-print">
                    <Link to="/courses" className="hover:text-ink transition-colors">Courses</Link>
                    <CaretRight size={12}/>
                    <Link to={`/courses/${courseId}`} className="hover:text-ink transition-colors">{course?.name}</Link>
                    <CaretRight size={12}/>
                    <span className="text-ink">{topic}</span>
                </div>

                <div className="flex items-start justify-between gap-6">
                    <div className="flex-1">
                        <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Lesson · {topic}</div>
                        <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-ink leading-tight" data-testid="lesson-title">{c.title}</h1>
                    </div>
                    <Button onClick={handlePrint} variant="outline" size="sm" className="rounded-sm border-ink/30 text-ink hover:bg-ink hover:text-bone no-print shrink-0" data-testid="btn-print-lesson">
                        {user?.is_premium || user?.is_admin
                            ? <><Printer size={16} className="mr-2" weight="duotone"/> Print / Save PDF</>
                            : <><Lock size={14} className="mr-2" weight="fill"/> Print · <span className="text-gold-dark ml-1">Premium</span></>
                        }
                    </Button>
                </div>

                <div className="print-only hidden text-xs text-ink/60 mt-2">
                    Wibpro Academy · {course?.name} · {course?.full_name}
                </div>

                <div className="mt-4 flex items-center gap-4 text-sm text-ink/60">
                    <span className="flex items-center gap-1"><Clock size={14}/> {c.reading_time_min || 8} min read</span>
                    <span className="flex items-center gap-1"><Sparkle size={14} weight="fill" className="text-gold"/> Claude Sonnet 4.6</span>
                </div>

                {c.summary && (
                    <div className="mt-8 p-6 border-l-4 border-gold bg-parchment/50 rounded-sm text-ink italic">
                        {c.summary}
                    </div>
                )}

                {/* Sections */}
                <div className="mt-12 space-y-10">
                    {(c.sections || []).map((s, i) => (
                        <div key={`section-${i}-${(s.heading || "").slice(0, 20)}`} className="animate-fade-up" data-testid={`section-${i}`}>
                            <div className="flex items-baseline gap-3 mb-3">
                                <span className="font-serif text-gold text-lg">{String(i + 1).padStart(2, "0")}</span>
                                <h2 className="font-serif text-2xl md:text-3xl text-ink leading-snug">{s.heading}</h2>
                            </div>
                            <p className="text-ink/80 leading-relaxed text-lg">{s.body}</p>
                        </div>
                    ))}
                </div>

                {/* Key facts */}
                {c.key_formulas_or_facts?.length > 0 && (
                    <div className="mt-14 border border-ink/15 rounded-sm bg-bone p-8" data-testid="facts-card">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold mb-4">
                            <BookOpen size={14} weight="duotone"/> Key facts & formulas
                        </div>
                        <ul className="space-y-2">
                            {c.key_formulas_or_facts.map((f, i) => (
                                <li key={`fact-${f.slice(0, 30)}-${i}`} className="flex items-start gap-3 text-ink">
                                    <span className="w-6 h-6 grid place-items-center rounded-full bg-ink text-bone text-[10px] font-mono shrink-0 mt-0.5">{i+1}</span>
                                    <span>{f}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Worked example */}
                {c.worked_example?.problem && (
                    <div className="mt-8 border border-ink rounded-sm bg-ink text-bone p-8" data-testid="worked-example-card">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold mb-4">
                            <ChalkboardTeacher size={14} weight="duotone"/> Worked example
                        </div>
                        <div className="font-serif text-lg mb-3">Problem</div>
                        <div className="text-bone/90 mb-6 whitespace-pre-wrap">{c.worked_example.problem}</div>
                        <div className="font-serif text-lg mb-3 text-gold">Solution</div>
                        <div className="text-bone/85 whitespace-pre-wrap leading-relaxed">{c.worked_example.solution}</div>
                    </div>
                )}

                {/* Common mistakes */}
                {c.common_mistakes?.length > 0 && (
                    <div className="mt-8 border border-oxblood/30 rounded-sm bg-bone p-8" data-testid="mistakes-card">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-oxblood mb-4">
                            <Warning size={14} weight="fill"/> Common mistakes to avoid
                        </div>
                        <ul className="space-y-2">
                            {c.common_mistakes.map((m, i) => (
                                <li key={`mistake-${m.slice(0, 30)}-${i}`} className="flex items-start gap-3 text-ink"><span className="text-oxblood mt-1">•</span>{m}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Study tips */}
                {c.study_tips?.length > 0 && (
                    <div className="mt-8 border border-emerald-800/30 rounded-sm bg-bone p-8" data-testid="tips-card">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-emerald-800 mb-4">
                            <Lightbulb size={14} weight="fill"/> Study tips
                        </div>
                        <ul className="space-y-2">
                            {c.study_tips.map((t, i) => (
                                <li key={`tip-${t.slice(0, 30)}-${i}`} className="flex items-start gap-3 text-ink"><span className="text-emerald-800 mt-1">✓</span>{t}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="mt-14 flex flex-wrap gap-3 no-print">
                    <Link to={`/courses/${courseId}`}>
                        <Button className="bg-ink text-bone hover:bg-ink/90 rounded-sm h-11" data-testid="btn-practice-topic">
                            Practice this topic now <ArrowRight size={16} className="ml-2"/>
                        </Button>
                    </Link>
                    <Link to={`/courses/${courseId}`}>
                        <Button variant="outline" className="rounded-sm h-11 border-ink/20 text-ink" data-testid="btn-back-course">
                            Back to course
                        </Button>
                    </Link>
                    <Button onClick={handlePrint} variant="ghost" className="rounded-sm h-11 text-ink hover:bg-ink/5" data-testid="btn-print-lesson-bottom">
                        {user?.is_premium || user?.is_admin
                            ? <><Printer size={16} className="mr-2"/> Print or save as PDF</>
                            : <><Lock size={14} className="mr-2"/> Save offline · <span className="text-gold-dark ml-1">Premium</span></>
                        }
                    </Button>
                </div>

                {/* Upsell dialog for free users */}
                <Dialog open={upsellOpen} onOpenChange={setUpsellOpen}>
                    <DialogContent className="max-w-md" data-testid="print-upsell-dialog">
                        <DialogHeader>
                            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold mb-2">
                                <Crown size={14} weight="fill"/> Premium perk
                            </div>
                            <DialogTitle className="font-serif text-3xl">Take this lesson offline.</DialogTitle>
                            <DialogDescription className="pt-3 text-ink/70 leading-relaxed">
                                Print or save this lesson as a polished, branded PDF — perfect for a night of focused revision without the internet.
                                <span className="block mt-3">Wibpro Premium also unlocks unlimited full-length mock exams and priority AI feedback.</span>
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter className="gap-2 sm:justify-start mt-4">
                            <Button className="bg-ink text-bone hover:bg-ink/90 rounded-sm" onClick={() => nav("/pricing")} data-testid="btn-upsell-upgrade">
                                Upgrade to Premium <ArrowRight size={16} className="ml-2"/>
                            </Button>
                            <Button variant="ghost" className="rounded-sm text-ink/70" onClick={() => setUpsellOpen(false)} data-testid="btn-upsell-dismiss">
                                Not now
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Print-only footer */}
                <div className="print-only hidden mt-16 pt-6 border-t border-ink/20 text-xs text-ink/60 text-center">
                    Wibpro Academy — AI-curated by Claude Sonnet 4.6.<br/>
                    Return to the app at your Wibpro Academy dashboard to attempt a practice test on this topic.
                </div>
            </section>
            <Footer />
        </div>
    );
}
